import type { SupabaseClient } from '@supabase/supabase-js';
import { isCoreMessagesEnabled } from '../messagesCore';

export interface RecentActivityContact {
  id: string;
  phone?: string | null;
  email?: string | null;
}

/** Same normalization Messages uses to group conversations. */
function normalizePhone(value: string | null | undefined): string {
  const digits = (value || '').replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

/**
 * Read-time supplement for the CRM "no activity" check.
 *
 * pipeline_opportunities.last_activity_at only moves when something inserts an
 * opportunity_activities row — and several message paths never do (inbound
 * SMS/email webhooks, automations, campaigns), so leads look stale despite a
 * live conversation. Rather than chase every write path (or touch triggers),
 * this reads the same truth the Messages view renders (core message events, or
 * all_messages for businesses still on the legacy source), matched to customers
 * by stored customer_id, normalized phone, or email.
 *
 * Returns customer_id -> latest message created_at within the window.
 */
export async function fetchLatestMessageActivityByCustomer(
  supabase: SupabaseClient,
  businessId: string,
  contacts: RecentActivityContact[],
  sinceIso: string,
): Promise<Map<string, string>> {
  const latestByCustomer = new Map<string, string>();
  if (contacts.length === 0) return latestByCustomer;

  const customerIds = new Set<string>();
  const customerByPhone = new Map<string, string>();
  const customerByEmail = new Map<string, string>();
  for (const contact of contacts) {
    customerIds.add(contact.id);
    const phone = normalizePhone(contact.phone);
    if (phone.length >= 7) customerByPhone.set(phone, contact.id);
    const email = (contact.email || '').toLowerCase().trim();
    if (email) customerByEmail.set(email, contact.id);
  }

  // Core businesses read the same events the Messages inbox is built from
  // (texts, emails, calls, callbacks), already keyed by customer / phone / email.
  if (await isCoreMessagesEnabled(businessId)) {
    const { data: events, error: eventsError } = await supabase
      .rpc('messages_core_events', { p_business_id: businessId })
      .gte('at', sinceIso)
      .order('at', { ascending: false })
      .limit(5000);
    if (eventsError) {
      console.error('[CRM no-activity] messages_core_events lookup failed:', eventsError);
      return latestByCustomer;
    }
    for (const row of (events ?? []) as Array<{ customer_id: string | null; phone_key: string | null; email_key: string | null; at: string }>) {
      const customerId =
        (row.customer_id && customerIds.has(row.customer_id) && row.customer_id)
        || (row.phone_key && customerByPhone.get(normalizePhone(row.phone_key)))
        || (row.email_key && customerByEmail.get(row.email_key))
        || undefined;
      if (customerId && !latestByCustomer.has(customerId) && row.at) {
        latestByCustomer.set(customerId, row.at);
        if (latestByCustomer.size === contacts.length) break;
      }
    }
    return latestByCustomer;
  }

  const { data, error } = await supabase
    .from('all_messages')
    .select('customer_id, from_number, to_number, created_at')
    .eq('business_id', businessId)
    .gte('created_at', sinceIso)
    .order('created_at', { ascending: false })
    .limit(5000);

  if (error) {
    console.error('[CRM no-activity] all_messages lookup failed:', error);
    return latestByCustomer;
  }

  // Rows are newest-first, so the first match per customer is their latest.
  const resolveCustomer = (row: {
    customer_id: string | null;
    from_number: string | null;
    to_number: string | null;
  }): string | undefined => {
    if (row.customer_id && customerIds.has(row.customer_id)) return row.customer_id;

    for (const raw of [row.from_number, row.to_number]) {
      if (!raw) continue;
      const value = raw.toLowerCase().trim();
      if (value.includes('@')) {
        // Email fields can be "Name <email>" or a bare address.
        for (const [email, customerId] of customerByEmail) {
          if (value.includes(email)) return customerId;
        }
      } else {
        const phone = normalizePhone(raw);
        if (phone) {
          const customerId = customerByPhone.get(phone);
          if (customerId) return customerId;
        }
      }
    }
    return undefined;
  };

  for (const row of data ?? []) {
    const customerId = resolveCustomer(row);
    if (customerId && !latestByCustomer.has(customerId) && row.created_at) {
      latestByCustomer.set(customerId, row.created_at);
      if (latestByCustomer.size === contacts.length) break;
    }
  }

  return latestByCustomer;
}
